import { Typography } from '@mui/material'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'
import { removeNewLinesFromEnd } from '@omnea-app/fe-rich-text/utils'
import { CommentImage } from './CommentImage'
import { CSS_CLASSES } from './constants'
import { TaskNoteType } from './isTaskNote'
import { SpanWithQuillStyles } from './SpanWithQuillStyles'
import { UserMention } from './UserMention'

// Full literal keys so the translation-key extraction still sees them.
const TASK_NOTE_LABEL_KEYS: Record<TaskNoteType, { withStepLabel: string; plain: string }> = {
  'task-rejection': { withStepLabel: 'request-comments.rejection-with-step-label', plain: 'request-comments.rejection' },
  'task-approval': { withStepLabel: 'request-comments.approval-with-step-label', plain: 'request-comments.approval' },
}

interface Props {
  commentText: string
  variant: 'MainBody' | 'Compact' | 'Sidebar'
  isAmendment?: boolean
  taskNoteType?: TaskNoteType
  stepLabel?: string
  showMoreInfoTooltip?: boolean
  mentionedUsers?: {
    id: string
    userId: string | null
    user: {
      firstName: string
      lastName: string
      email: string
      isOutOfOffice: boolean
    } | null
  }[]
}

export function CommentStringWithMentions({
  commentText,
  variant,
  isAmendment = false,
  taskNoteType,
  stepLabel,
  showMoreInfoTooltip = false,
  mentionedUsers,
}: Props) {
  const { t } = useTranslation('requests')
  const fontVariant = variant === 'MainBody' || variant === 'Compact' ? 'body-regular' : 'small-regular'

  const htmlWithStyledMentions = styleMentions(commentText)
  const trimmedValue = removeNewLinesFromEnd(htmlWithStyledMentions)

  const parsedHtml = parse(trimmedValue, {
    trim: true,
    replace: (node) => {
      // strip off any style tags that might make the comment unreadable
      if ('attribs' in node && node.attribs && node.attribs.style) {
        delete node.attribs.style
      }

      // Render inline images (referenced by filestoreId) as capped,
      // click-to-expand thumbnails resolved to a signed URL.
      if ('attribs' in node && node.name === 'img' && node.attribs?.['data-filestore-id']) {
        return <CommentImage filestoreId={node.attribs['data-filestore-id']} alt={node.attribs.alt} />
      }

      // Handle comment mentions by wrapping them with Tooltip
      if (
        'attribs' in node && // 1. Check if the parsed HTML node has attributes
        node.children && // 2. Ensure the node has children
        node.attribs && // 3. Check if attributes object exists
        node.attribs.class === CSS_CLASSES.COMMENT_MENTION && // 4. Check if it's a mention element
        node.attribs['data-user-id'] // 5. Ensure the data-user-id attribute exists
      ) {
        const mentionedUser = mentionedUsers?.find((user) => user.userId === node.attribs['data-user-id'])
        const fallbackText = node.children && node.children[0] && 'data' in node.children[0] ? node.children[0].data : ''
        return <UserMention mentionedUser={mentionedUser} showMoreInfoTooltip={showMoreInfoTooltip} fallbackText={fallbackText} />
      }
    },
  })

  return (
    <Typography variant={fontVariant}>
      {isAmendment && <strong> {t('request-comments.amendment-requested')}: </strong>}
      {taskNoteType && (
        <strong>
          {' '}
          {stepLabel
            ? t(TASK_NOTE_LABEL_KEYS[taskNoteType].withStepLabel, { stepLabel })
            : t(TASK_NOTE_LABEL_KEYS[taskNoteType].plain)}
          :{' '}
        </strong>
      )}
      <SpanWithQuillStyles
        sx={{
          wordBreak: 'break-word',
          lineHeight: '20px',
          '& a': {
            color: 'text.brand.default',
          },
          [`& .${CSS_CLASSES.COMMENT_MENTION}`]: {
            padding: '2px 6px',
            backgroundColor: 'bg.surface.subtle',
            borderRadius: '4px',
            fontWeight: '500',
            color: 'text.default',
            cursor: showMoreInfoTooltip ? 'pointer' : 'default',
          },
          '& .step-name': { color: 'text.brand.default' },
          img: {
            maxWidth: '100%',
            height: 'auto',
          },
        }}
      >
        {parsedHtml}
      </SpanWithQuillStyles>
    </Typography>
  )
}

export const MENTION_REGEX = /@\[(.*?)]\((.*?)\)/g

const styleMentions = (comment: string) => {
  const result = comment.replace(MENTION_REGEX, (_, name, id) => {
    return `<span class="${CSS_CLASSES.COMMENT_MENTION}" data-user-id="${id}">@${name}</span>`
  })
  return result
}
